/**
 * A decision Lykke puts in front of the client: creators to approve, drafts to review, creator payments to release.
 * The buttons call the platform's own client endpoints with the person's token (beatlyApi.ts), so the platform
 * records the decision as theirs. Lykke never presses them. Asking for changes or raising an issue needs a reason.
 */
import { useState } from 'react';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { acceptCuratedInfluencer, approveClientPayout, postClientDraftApprove, postClientDraftReject, raiseClientPayoutIssue, rejectCuratedInfluencer } from '@/api/beatlyApi';
import { getTokenFromStorage } from '@/utils/auth';
import { cn } from '@/components/ui/utils';
import type { DecisionCardData, DecisionItem } from './lykkeApi';
import { lykkePath } from './lykkePages';

type Done = 'approved' | 'declined' | 'changes' | 'released' | 'issue';
const DONE_LABEL: Record<Done, string> = { approved: 'Approved', declined: 'Declined', changes: 'Changes asked', released: 'Released', issue: 'Issue raised' };
const TITLE: Record<DecisionCardData['kind'], (n: number) => string> = {
  creators: (n) => `${n} creator${n === 1 ? '' : 's'} to approve`,
  drafts: (n) => `${n} draft${n === 1 ? '' : 's'} to review`,
  payments: (n) => `${n} creator payment${n === 1 ? '' : 's'} to release`,
};

export function DecisionCard({ card }: { card: DecisionCardData }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [done, setDone] = useState<Record<string, Done>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const act = async (item: DecisionItem, what: Done) => {
    const token = getTokenFromStorage();
    if (!token) { setError('Sign in again to decide.'); return; }
    setBusy(item.id); setError(null);
    try {
      if (card.kind === 'creators') await (what === 'approved' ? acceptCuratedInfluencer(item.id, token) : rejectCuratedInfluencer(item.id, token, reason.trim() ? { reason: reason.trim() } : {}));
      else if (card.kind === 'drafts') await (what === 'approved' ? postClientDraftApprove(item.id, token, { feedback: '' }) : postClientDraftReject(item.id, token, { feedback: reason.trim() }));
      else await (what === 'released' ? approveClientPayout(item.id, token) : raiseClientPayoutIssue(item.id, reason.trim(), token));
      setDone((d) => ({ ...d, [item.id]: what }));
      setAsking(null); setReason('');
      void qc.invalidateQueries({ queryKey: ['lykke'] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'notifications'] });
    } catch (e) {
      setError((e as Error).message?.slice(0, 140) || 'That did not go through. Try again.');
    } finally { setBusy(null); }
  };

  const open = (item?: DecisionItem) => {
    const page = card.kind === 'creators' ? 'campaign-profiles' : card.kind === 'drafts' ? 'campaign-contentApproval' : 'campaign-overview';
    const focus: Record<string, string> = card.kind === 'drafts' && item?.productId ? { draftProductId: item.productId, draftVersionId: item.id } : card.kind === 'creators' && item?.influencerId ? { creatorProfile: item.influencerId } : {};
    const to = lykkePath(page, { campaignId: card.campaignId }, focus);
    if (to) navigate(to);
  };

  const needsReason = (what: Done) => what === 'changes' || what === 'issue';
  const left = card.items.filter((i) => !done[i.id]).length;

  return (
    <div className="mt-2 w-full overflow-hidden rounded-xl border border-[#eaecf0] bg-white" data-lykke-decision={card.kind}>
      <div className="flex items-center justify-between gap-2 border-b border-[#f2f4f7] px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-[#101828]">{left ? TITLE[card.kind](left) : 'All decided'}</div>
          <div className="truncate text-[12px] text-[#667085]">{card.campaignName}</div>
        </div>
        <button type="button" onClick={() => open()} className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-[#7a1fbf] hover:underline">
          Open page <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      <ul className="divide-y divide-[#f2f4f7]">
        {card.items.map((item) => {
          const d = done[item.id];
          const positive: Done = card.kind === 'payments' ? 'released' : 'approved';
          const negative: Done = card.kind === 'creators' ? 'declined' : card.kind === 'drafts' ? 'changes' : 'issue';
          const negLabel = card.kind === 'creators' ? 'Decline' : card.kind === 'drafts' ? 'Ask for changes' : 'Raise issue';
          return (
            <li key={item.id} className="px-3 py-2">
              {/* Wraps in a narrow panel: the buttons go under the name instead of out of the card. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {item.preview ? (
                  <button type="button" onClick={() => open(item)} className="h-12 w-9 shrink-0 overflow-hidden rounded-md bg-[#f2f4f7]" aria-label={`Open ${item.title}`}>
                    <img src={item.preview} alt="" className="h-full w-full object-cover" />
                  </button>
                ) : null}
                <button type="button" onClick={() => open(item)} className="min-w-[110px] flex-1 text-left">
                  <div className="truncate text-[13px] font-medium text-[#101828]">{item.title}</div>
                  {item.sub && <div className="truncate text-[12px] text-[#667085]">{item.sub}</div>}
                </button>
                {d ? (
                  <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11.5px] font-semibold', d === 'approved' || d === 'released' ? 'bg-[#a9f600]/30 text-[#3b5a00]' : 'bg-[#f2f4f7] text-[#344054]')}>{DONE_LABEL[d]}</span>
                ) : busy === item.id ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#98a2b3]" />
                ) : (
                  <div className="ml-auto flex shrink-0 items-center gap-1">
                    <button type="button" onClick={() => (needsReason(negative) ? (setAsking(asking === item.id ? null : item.id), setReason('')) : void act(item, negative))}
                      className="inline-flex h-7 items-center gap-1 rounded-full border border-[#d0d5dd] px-2.5 text-[12px] font-semibold text-[#344054] hover:bg-[#f9fafb]">
                      <X className="h-3 w-3" />{negLabel}
                    </button>
                    <button type="button" onClick={() => void act(item, positive)}
                      className="inline-flex h-7 items-center gap-1 rounded-full bg-[#be43ff] px-2.5 text-[12px] font-semibold text-white hover:bg-[#a830e0]">
                      <Check className="h-3 w-3" />{card.kind === 'payments' ? 'Release' : 'Approve'}
                    </button>
                  </div>
                )}
              </div>
              {asking === item.id && !d && (
                <div className="mt-2 flex items-end gap-2">
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} autoFocus
                    placeholder={card.kind === 'drafts' ? 'What should the creator change?' : 'What is wrong?'}
                    className="min-h-[52px] flex-1 resize-none rounded-lg border border-[#d0d5dd] px-2.5 py-1.5 text-[13px] text-[#101828] outline-none focus:border-[#be43ff]" />
                  <button type="button" disabled={!reason.trim()} onClick={() => void act(item, negative)}
                    className="h-8 shrink-0 rounded-full bg-[#101828] px-3 text-[12px] font-semibold text-white disabled:opacity-40">Send</button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {error && <div className="border-t border-[#f2f4f7] px-3 py-2 text-[12px] text-[#b42318]">{error}</div>}
    </div>
  );
}
